import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import api from '../lib/api';
import { money } from './PlatformConsole';

const STREAMS = [['plans', 'Plans'], ['addons', 'Add-ons'], ['branch_slots', 'Paid locations']];
const monthLabel = (key) => new Date(`${key}-01T00:00:00Z`)
  .toLocaleDateString('en-GB', { month: 'short', year: '2-digit', timeZone: 'UTC' });

/** Compact card for the console overview. */
export const RevenueCard = () => {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get('/platform/revenue', { params: { months: 6 } })
      .then(r => setData(r.data)).catch(() => setData(null));
  }, []);
  if (!data) return null;
  const now = data.this_month;
  const peak = Math.max(1, ...data.series.map(s => s.total));
  return (
    <div className="pc-card" data-testid="pc-revenue-card">
      <div className="pc-card-h">
        <h2>Collected this month</h2>
        <Link className="pc-btn" to="/platform/revenue" data-testid="pc-revenue-more">Month by month</Link>
      </div>
      <div style={{ padding: '.9rem', display: 'grid', gap: '.85rem' }}>
        <div>
          <p className="pc-kpi" data-testid="pc-revenue-total">{money(now.total)}</p>
          <p className="pc-sub">
            {monthLabel(now.month)}
            {data.change_pct != null && (
              <span data-testid="pc-revenue-change">
                {' · '}{data.change_pct >= 0 ? '+' : ''}{data.change_pct}% vs last month
              </span>
            )}
          </p>
        </div>
        <div style={{ display: 'grid', gap: '.35rem' }}>
          {STREAMS.map(([key, label]) => (
            <div key={key} style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem' }}
              data-testid={`pc-revenue-stream-${key}`}>
              <span className="pc-muted">{label}</span>
              <b className="pc-mono">{money(now[key])}</b>
            </div>
          ))}
        </div>
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 4, height: 46 }} aria-hidden="true">
          {data.series.map(s => (
            <span key={s.month} title={`${monthLabel(s.month)} · ${money(s.total)}`}
              style={{ flex: 1, background: s.month === now.month ? '#38bdf8' : '#1e3a5f',
                       height: `${Math.max(4, (s.total / peak) * 100)}%`, borderRadius: 3 }} />
          ))}
        </div>
        <p className="pc-muted" style={{ fontSize: '.72rem' }}>{data.note}</p>
      </div>
    </div>
  );
};

export const PlatformRevenue = () => {
  const [data, setData] = useState(null);
  const [months, setMonths] = useState(12);

  useEffect(() => {
    setData(null);
    api.get('/platform/revenue', { params: { months } })
      .then(r => setData(r.data)).catch(() => toast.error('Could not load revenue'));
  }, [months]);

  if (!data) return <div className="pc-muted" data-testid="pc-revenue-loading"><Loader2 className="h-4 w-4 animate-spin" /></div>;
  const total = data.series.reduce((sum, s) => sum + s.total, 0);

  return (
    <div data-testid="pc-revenue">
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: '1rem', flexWrap: 'wrap' }}>
        <div>
          <h1 className="pc-h1">Revenue</h1>
          <p className="pc-sub">{data.note}</p>
        </div>
        <select className="pc-select" style={{ width: 160 }} value={months}
          onChange={e => setMonths(Number(e.target.value))} data-testid="pc-revenue-range">
          {[6, 12, 24].map(m => <option key={m} value={m}>Last {m} months</option>)}
        </select>
      </div>

      <div className="pc-card" style={{ marginTop: '1.1rem' }}>
        <div className="pc-card-h"><h2>{monthLabel(data.this_month.month)} so far</h2>
          <b className="pc-mono" data-testid="pc-revenue-month-total">{money(data.this_month.total)}</b></div>
        <table className="pc-table">
          <thead><tr><th>Month</th>{STREAMS.map(([k, l]) => <th key={k} className="pc-num">{l}</th>)}
            <th className="pc-num">Recorded by hand</th><th className="pc-num">Total</th></tr></thead>
          <tbody>
            {[...data.series].reverse().map(row => (
              <tr key={row.month} data-testid={`pc-revenue-row-${row.month}`}>
                <td>{monthLabel(row.month)}</td>
                {STREAMS.map(([k]) => <td key={k} className="pc-num pc-mono">{money(row[k])}</td>)}
                <td className="pc-num pc-mono">{money(row.manual)}</td>
                <td className="pc-num pc-mono"><b>{money(row.total)}</b></td>
              </tr>
            ))}
            <tr>
              <td><b>Period</b></td>
              {STREAMS.map(([k]) => (
                <td key={k} className="pc-num pc-mono">
                  <b>{money(data.series.reduce((sum, s) => sum + s[k], 0))}</b>
                </td>
              ))}
              <td className="pc-num pc-mono"><b>{money(data.series.reduce((sum, s) => sum + s.manual, 0))}</b></td>
              <td className="pc-num pc-mono"><b>{money(total)}</b></td>
            </tr>
          </tbody>
        </table>
      </div>

      <div className="pc-card" style={{ marginTop: '1.1rem' }}>
        <div className="pc-card-h"><h2>Top paying pharmacies</h2></div>
        <table className="pc-table">
          <thead><tr><th>Pharmacy</th><th className="pc-num">Collected</th></tr></thead>
          <tbody>
            {data.top_payers.map(p => (
              <tr key={p.tenant_id} data-testid={`pc-revenue-payer-${p.tenant_id}`}>
                <td><Link className="pc-link" to={`/platform/pharmacies/${p.tenant_id}`}>{p.name}</Link></td>
                <td className="pc-num pc-mono">{money(p.collected_ghs)}</td>
              </tr>
            ))}
            {data.top_payers.length === 0 && (
              <tr><td colSpan={2} className="pc-muted" style={{ textAlign: 'center', padding: '1.6rem' }}
                data-testid="pc-revenue-no-payers">No payments collected in this period yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};
